import React from 'react';
import classNames from 'classnames';

import Store from 'store';
import Data from 'data';
import Locale from 'data/locale';
import { Color } from 'common';
import Select, { underlinerFunc } from 'ui/select';
import { monsterFix, monsterList, monsterSetArea, monsterSetId, monsterSetMod, monsterSetType, selectMonster,
  monsterMods, monsterItems, monsterLevel, monsterRandomItem, monsterFixItems, monsterHealth, monsterAttacks, monsterPresetMods } from 'logic/monster/picker';
import { ItemModsSection, ItemSuperiorSection } from 'components/equipment/ItemEditor';

import './Monster.scss';
import { monsterAreaList, monsterBossList, monsterUniqueList, terrorZoneList, uniqueMonsterAuras } from 'logic/monster/monsterList';
import { itemSlotGroups } from 'logic/itemTypes';
import { itemAffixLevel, itemGetModGroups } from 'logic/item';
import { itemUpdate } from 'logic/itemReducers';
import { D2Tooltip, ItemTooltip } from 'tooltips';
import { gameQualityMap } from 'logic/binary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDice } from '@fortawesome/free-solid-svg-icons';
import NumberInput from 'ui/number';
import Tooltip from 'ui/tooltip';

const _L = Locale("ui-calculator");
const _Le = Locale("ui-equipment");

const renderOption = (opt, search) => <span className={`d2-color-${opt.color || 0}`}>{search ? underlinerFunc(opt.name, search) : opt.name}</span>;
const renderAura = (opt, search) => (<span className={`d2-color-${opt.color || 0}`}>
  {search ? underlinerFunc(opt.name, search) : opt.name} <span className={`d2-color-${Color.GRAY}`}>{_L`(Level ${opt.level})`}</span>
</span>);

function ItemModEditor({ item, onChange }) {
  const alvl = itemAffixLevel(item.base, item.ilvl);
  const groups = itemGetModGroups(item.base, item.quality, item.ilvl, mod => alvl >= mod.level && (mod.maxlevel == null || alvl <= mod.maxlevel));
  if (item.mods) {
    return <ItemModsSection item={item} onChange={onChange} modGroups={groups}/>;
  } else if (item.superior) {
    return <ItemSuperiorSection item={item} onChange={onChange} modGroups={groups}/>;
  } else {
    return null;
  }
}

const MonsterItemTooltip = ({ item }) => <ItemTooltip item={item && itemUpdate({...item})}/>;
const ItemTemplateTooltip = ({ base, quality }) => <ItemTooltip item={itemUpdate({base, quality: gameQualityMap[quality], template: true})}/>;

function monsterItemOption(opt, search) {
  const m = opt?.id.match(/^(.*):(\d+)$/);
  if (!m) return renderOption(opt, search);
  return <D2Tooltip tooltip={<ItemTemplateTooltip base={m[1]} quality={parseInt(m[2])}/>}>{renderOption(opt, search)}</D2Tooltip>;
}

function randomItemOption(opt, prev) {
  const m = opt?.id.match(/^(.*):(\d+)$/);
  if (!m) return prev;
  return monsterRandomItem(m[1], gameQualityMap[parseInt(m[2])], prev.ilvl);
}

const itemOption = (base, quality) => ({id: `${base}:${quality}`, name: Locale.strings[Data.items[base]?.namestr], color: Data.qualityColor[quality]});

export function UnitItems({ id, level, unitLevel, items, onChange }) {
  const unitItems = React.useMemo(() => monsterItems(id, unitLevel), [id, unitLevel]);
  const [slot, setSlot] = React.useState(Object.keys(unitItems)[0]);

  React.useEffect(() => {
    if (unitItems && !unitItems[slot]) {
      setSlot(Object.keys(unitItems)[0]);
    }
  }, [unitItems, slot]);

  const slotOptions = React.useCallback(() => Object.keys(unitItems).map(slot => ({id: slot, name: _Le(itemSlotGroups[slot].name)})), [unitItems]);
  const onSetSlot = React.useCallback(value => setSlot(value.id), []);

  const itemList = unitItems[slot];
  const itemOptions = React.useCallback(() => itemList?.map(([base, quality]) => itemOption(base, quality)) || [], [itemList]);
  const item = items?.[slot];
  const renderValue = React.useCallback(opt => <D2Tooltip tooltip={<MonsterItemTooltip item={item}/>}>{renderOption(opt)}</D2Tooltip>, [item]);

  const updateItem = React.useCallback(update => onChange(items => {
    const prev = items[slot];
    if (!prev) return items;
    const item = (typeof update === 'function' ? update(prev) : update);
    if (item === prev) return items;
    return {...items, [slot]: item};
  }), [slot, onChange]);
  const randomAll = React.useCallback(() => onChange(() => {
    const monster = {id};
    monsterFixItems(monster, level, unitLevel);
    return monster.items;
  }), [onChange, id, level, unitLevel]);
  //const randomCurrent = React.useCallback(() => updateItem(item => monsterRandomItem(item.base, item.quality, item.ilvl)), [updateItem]);

  if (!unitItems || !item) return null;

  return (
    <div className="d2p-MonsterItems">
      <div className="d2p-item-picker">
        <Select options={slotOptions}
                value={slot && ({id: slot, name: _Le(itemSlotGroups[slot].name)})}
                onChange={onSetSlot}
                disabled={Object.keys(unitItems).length <= 1}
                className="d2p-item-slot"
                />
        <Select options={itemOptions}
                value={item && itemOption(item.base, item.quality)}
                onChange={opt => updateItem(item => randomItemOption(opt, item))}
                renderOption={monsterItemOption}
                renderValue={renderValue}
                disabled={item && itemList.length === 1}
                className="d2p-item-item"
                />
        <span className="d2p-item-extra">
          <FontAwesomeIcon icon={faDice} className="d2p-item-random" onClick={randomAll}/>
        </span>
      </div>
      <ItemModEditor item={item} onChange={updateItem}/>
    </div>
  );
}

function MonsterItems({ id, monster, difficulty, playerLevel }) {
  const dispatch = Store.useDispatch();
  const level = monsterLevel(monster, difficulty, playerLevel);

  const onChange = React.useCallback(update => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    const prev = monster.items;
    if (!prev) return profile;
    const items = update(prev);
    if (items === prev) return profile;
    return {...profile, monster: {...monster, items}};
  }), [dispatch]);

  return <UnitItems id={id} level={level} unitLevel={level} items={monster.items} onChange={onChange}/>;
}

export default React.memo(function MonsterSection() {
  const omonster = Store.useProfile(profile => profile.monster);
  const monster = Store.useProfile(selectMonster);
  const difficulty = Store.useProfile(profile => profile.difficulty);
  const playerLevel = Store.useProfile(profile => profile.level);

  const monsterTypes = React.useMemo(() => [
    {id: 'dummy', name: _L`Target Dummy`, color: Color.WHITE},
    {id: 'normal', name: _L`Normal`, color: Color.WHITE},
    {id: 'champion', name: _L`Champion`, color: Color.BLUE},
    {id: 'unique', name: _L`Unique`, color: Color.GOLD},
    {id: 'super', name: _L`Super Unique`, color: Color.GOLD},
    {id: 'boss', name: _L`Boss`, color: Color.GOLD},
  ], []);

  const areas = React.useCallback(() => {
    const acts = [
      {title: _L`Act I`, options: []},
      {title: _L`Act II`, options: []},
      {title: _L`Act III`, options: []},
      {title: _L`Act IV`, options: []},
      {title: _L`Act V`, options: []},
    ];
    const areaList = monster.terrorized ? terrorZoneList : monsterAreaList;
    for (const id of areaList) {
      const row = Data.levels[id];
      if (!monsterList({type: monster.type, area: id}, difficulty).length) continue;
      acts[row.act || 0].options.push({id: id.toString(), name: Locale.strings[row.levelname]});
    }
    return acts;
  }, [monster.type, monster.terrorized, difficulty]);

  const monsters = React.useCallback(() => monsterList(monster, difficulty).map(id => ({id, name: Locale.strings[Data.monsters[id].namestr]})), [monster, difficulty]);

  const champions = React.useMemo(() => [
    {id: 'champion', name: Locale.strings.Champion},
    {id: 'berserk', name: Locale.strings.Berserk},
    {id: 'fanatic', name: Locale.strings.Fanatic},
    {id: 'ghostly', name: Locale.strings.Ghostly},
    {id: 'possessed', name: Locale.strings.Possessed},
  ], []);

  const uniques = React.useMemo(() => [
    {id: 'strong', name: Locale.strings.uniquextrastrong},
    {id: 'fast', name: Locale.strings.uniqueextrafast},
    {id: 'curse', name: Locale.strings.uniquecursed},
    {id: 'resist', name: Locale.strings.uniquemagicresistance},
    {id: 'fire', name: Locale.strings.uniquefireenchanted},
    {id: 'lightning', name: Locale.strings.monsteruniqueprop2},
    {id: 'cold', name: Locale.strings.monsteruniqueprop1},
    {id: 'poisonhit', name: _L`Poison Enchanted`},
    {id: 'manahit', name: Locale.strings.monsteruniqueprop3},
    {id: 'teleport', name: Locale.strings.monsteruniqueprop5},
    {id: 'spectralhit', name: Locale.strings.monsteruniqueprop4},
    {id: 'stoneskin', name: Locale.strings.monsteruniqueprop6},
    {id: 'multishot', name: Locale.strings.monsteruniqueprop7},
    {id: 'aura', name: Locale.strings.monsteruniqueprop9},
  ], []);

  const supers = React.useCallback(() => {
    const acts = [
      {title: _L`Act I`, options: []},
      {title: _L`Act II`, options: []},
      {title: _L`Act III`, options: []},
      {title: _L`Act IV`, options: []},
      {title: _L`Act V`, options: []},
    ];
    for (const [id, {area}] of Object.entries(monsterUniqueList)) {
      const uinfo = Data.superUniques[id], ainfo = Data.levels[area];
      if (!uinfo || !ainfo) continue;
      if (monster.terrorized && !terrorZoneList.includes(area)) continue;
      acts[ainfo.act || 0].options.push({id, name: Locale.strings[uinfo.name]});
    }
    for (const act of acts) {
      const aid = id => monsterAreaList.indexOf(monsterUniqueList[id].area);
      act.options.sort((a, b) => aid(a.id) - aid(b.id));
    }
    return acts;
  }, [monster.terrorized]);

  const bosses = React.useMemo(() => {
    function makeCategory(title, ids) {
      if (monster.terrorized) {
        ids = ids.filter(id => terrorZoneList.includes(monsterBossList[id].area));
      }
      if (!ids.length) return null;
      return {title, options: ids.map(id => ({id, name: Locale.strings[Data.monsters[id].namestr]}))};
    }
    return [
      makeCategory('Bosses', ['bloodraven', 'griswold', 'radament', 'summoner', 'izual', 'nihlathakboss']),
      makeCategory('Act Bosses', ['andariel', 'duriel', 'mephisto', 'diablo', 'baalcrab']),
      makeCategory('World', ['diabloclone']),
      makeCategory('Pandemonium', ['ubermephisto', 'uberdiablo', 'uberbaal', 'uberandariel', 'uberduriel', 'uberizual']),
    ].filter(Boolean);
  }, [monster.terrorized]);

  const auras = React.useMemo(() => {
    const level = monsterLevel(monster, difficulty, playerLevel);
    return Object.keys(uniqueMonsterAuras).map(id => {
      const desc = Data.skillDesc[Data.skills[id]?.skilldesc];
      return {id, name: Locale.strings[desc?.strname], level: Math.max((monster.type === 'boss' && monsterBossList[monster.id]?.auralevel) || 1, (level / uniqueMonsterAuras[id].divide) | 0)};
    });
  }, [monster, difficulty, playerLevel]);
  const aurasList = React.useCallback(() => {
    const level = monsterLevel(monster, difficulty, playerLevel);
    return auras.filter(opt => level >= (uniqueMonsterAuras[opt.id].min || 0));
  }, [auras, monster, difficulty, playerLevel]);

  const bnetOptions = React.useMemo(() => [
    {id: 'normal', name: _L`Regular area`},
    {id: 'terror', name: _L`Terror zone`},
    {id: 'single', name: _L`Single player`},
  ], []);
  const playerOptions = React.useMemo(() => [...Array(8)].map((_, i) => ({id: i + 1, name: _L`Players ${i + 1}`})), []);

  const dispatch = Store.useDispatch();
  const onChangeType = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.type === t.id) return profile;
    return {...profile, monster: monsterSetType(monster, t.id, profile.difficulty, profile.level)};
  }), [dispatch]);
  const onChangeLevel = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.level === t) return profile;
    return {...profile, monster: {...monster, level: t}};
  }), [dispatch]);
  const onToggleAuto = React.useCallback(t => dispatch.setProfile(profile => {
    if (profile.monster) {
      return {...profile, monster: null};
    } else {
      return {...profile, monster: selectMonster(profile)};
    }
  }), [dispatch]);
  const onChangeArea = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.area === t.id) return profile;
    return {...profile, monster: monsterSetArea(monster, t.id, profile.difficulty, profile.level)};
  }), [dispatch]);
  const onChangeId = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.id === t.id) return profile;
    return {...profile, monster: monsterSetId(monster, t.id, profile.difficulty, profile.level)};
  }), [dispatch]);
  const onChangeAura = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.aura === t.id) return profile;
    return {...profile, monster: {...monster, aura: t.id}};
  }), [dispatch]);
  const onChangeChampion = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.champion === t.id) return profile;
    return {...profile, monster: {...monster, champion: t.id}};
  }), [dispatch]);
  const onChangeMod = React.useMemo(() => [0, 1, 2].map(index => (t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.mods?.[index] === t?.id) return profile;
    return {...profile, monster: monsterSetMod(monster, index, t?.id, profile.difficulty, profile.level)};
  }))), [dispatch]);
  const onChangeBnet = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    const single = t.id === 'single', terrorized = t.id === 'terror';
    if (monster.single === single && monster.terrorized === terrorized) return profile;
    return {...profile, monster: monsterFix({...monster, single, terrorized, maxhp: 0}, profile.difficulty, profile.level)};
  }), [dispatch]);
  const onChangePlayers = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    const players = parseInt(t.id);
    if (monster.players === players) return profile;
    return {...profile, monster: {...monster, players}};
  }), [dispatch]);
  const onChangeHealth = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.maxhp === t) return profile;
    return {...profile, monster: {...monster, maxhp: t}};
  }), [dispatch]);
  const onChangeAttack = React.useCallback(t => dispatch.setProfile(profile => {
    const monster = selectMonster(profile);
    if (monster.attack === t.id) return profile;
    return {...profile, monster: {...monster, attack: t.id}};
  }), [dispatch]);

  const bossValue = Data.monsters[monster.id] ? {id: monster.id, name: Locale.strings[Data.monsters[monster.id].namestr]} : null;
  if (monster.type === 'boss') {
    const index = bosses.findIndex(cat => cat.options.some(opt => opt.id === monster.id));
    if (index >= 2) {
      bossValue.name += ` (${bosses[index].title})`;
    }
  }

  const {presetMods, extraMods, presetAura} = React.useMemo(() => {
    if (monster.type === 'super') {
      return {
        presetMods: monsterPresetMods(monster.type, monster.id),
        presetAura: monsterUniqueList[monster.id]?.aura?.toString(),
        extraMods: difficulty,
      };
    } else if (monster.type === 'boss') {
      const uinfo = monsterBossList[monster.id];
      return {
        presetMods: monsterPresetMods(monster.type, monster.id),
        presetAura: uinfo?.aura?.toString(),
        extraMods: (uinfo?.unique ? difficulty : 0),
      };
    } else if (monster.type === 'unique') {
      return {extraMods: difficulty + 1};
    } else {
      return {};
    }
  }, [monster.type, monster.id, difficulty]);

  const uniquesShow = React.useMemo(() => {
    const allowed = monsterMods(monster.type, monster.id, difficulty);
    return [0, 1, 2].map(idx => allowed.map(id => {
      const opt = {...uniques.find(u => u.id === id)};
      if (monster.mods?.find((oid, oi) => idx !== oi && oid === id)) {
        opt.disabled = true;
      } else if (presetMods?.includes(id)) {
        opt.disabled = true;
      }
      return opt;
    }));
  }, [monster.mods, monster.type, monster.id, difficulty, uniques, presetMods]);

  function renderMod(index, preset) {
    const id = (preset ? presetMods?.[index] : monster.mods?.[index]);
    const show = !!(preset ? id && uniques.some(t => t.id === id) : index < extraMods);
    if (index === 3 && !id) return null;

    return (
      <div className="d2p-monster-mod" key={index}>
        {show && (
          <Select options={preset ? uniques : uniquesShow[index]}
                  value={uniques.find(t => t.id === id)}
                  onChange={preset ? null : onChangeMod[index]}
                  placeholder={_L`Select Modifier`}
                  disabled={preset}
                  allowEmpty
                  />
        )}
        {show && id === 'aura' && (
          <Select options={aurasList}
                  value={auras.find(t => t.id === (presetAura || monster.aura))}
                  onChange={presetAura ? null : onChangeAura}
                  placeholder={_L`Select Aura`}
                  disabled={!!presetAura}
                  renderValue={renderAura}
                  renderOption={renderAura}
                  />
        )}
      </div>
    );
  }

  const monId = (monster.type === 'super' ? Data.superUniques[monster.id]?.class : monster.id);
  const monInfo = Data.monsters[monId];
  const health = React.useMemo(() => monsterHealth(monster, difficulty, playerLevel), [monster, difficulty, playerLevel]);
  const attacks = React.useMemo(() => monsterAttacks(monster, difficulty), [monster, difficulty]);
  const attackOptions = React.useMemo(() => [
    {id: 'a1', name: _L`Attack 1`},
    {id: 'a2', name: _L`Attack 2`},
    {id: 's1', name: _L`Skill 1`},
  ].filter(opt => attacks.includes(opt.id)), [attacks]);

  return (
    <div className="d2p-MonsterSection">
      <div className="d2p-monster-options">
        {!Data.info.pd2 && (
          <Tooltip tooltip={_L`In Battle.net games most monsters have 25% less HP on Hell difficulty, and 15% less attack rating on Nightmare and Hell.`}>
            <Select options={bnetOptions}
                    value={bnetOptions[monster.terrorized ? 1 : monster.single ? 2 : 0]}
                    onChange={onChangeBnet}
                    className="d2p-monster-bnet"
                    />
          </Tooltip>
        )}
        <Tooltip tooltip={_L`Each additional player increases monster HP and EXP by 50%, and damage and attack rating by 6.25%`}>
          <Select options={playerOptions}
                  value={playerOptions[(monster.players || 1) - 1]}
                  onChange={onChangePlayers}
                  className="d2p-monster-players"
                  />
        </Tooltip>
        <div className="d2p-monster-id"/>
      </div>
      <div className="d2p-monster-options">
        <Select options={monsterTypes}
                value={monsterTypes.find(t => t.id === monster.type)}
                onChange={onChangeType}
                renderOption={renderOption}
                renderValue={renderOption}
                className="d2p-monster-type"
                />
        {(monster.type === 'normal' || monster.type === 'champion' || monster.type === 'unique') && (
          <React.Fragment>
            <Select options={areas}
                    value={Data.levels[monster.area] ? {id: monster.area, name: Locale.strings[Data.levels[monster.area].levelname]} : null}
                    onChange={onChangeArea}
                    className="d2p-monster-area"
                    searchLimit={10}
                    />
            <Select options={monsters}
                    value={Data.monsters[monster.id] ? {id: monster.id, name: Locale.strings[Data.monsters[monster.id].namestr]} : null}
                    onChange={onChangeId}
                    className="d2p-monster-id"
                    searchLimit={10}
                    />
          </React.Fragment>
        )}
        {monster.type === 'super' && (
          <Select options={supers}
                  value={Data.superUniques[monster.id] ? {id: monster.id, name: Locale.strings[Data.superUniques[monster.id].name]} : null}
                  onChange={onChangeId}
                  className="d2p-monster-area"
                  searchLimit={10}
                  />
        )}
        {monster.type === 'boss' && (
          <Select options={bosses}
                  value={bossValue}
                  onChange={onChangeId}
                  className="d2p-monster-area"
                  searchLimit={10}
                  />
        )}
        {monster.type === 'dummy' && (
          <div className="d2p-monster-level">
            {_L`Level`} <NumberInput value={monster.level} min={1} max={99} onChange={onChangeLevel} disabled={!omonster}/>{' '}
            <span className={classNames("d2p-monster-auto", {"d2p-enabled": !omonster})} onClick={onToggleAuto}>({_L`auto`})</span>
          </div>
        )}
        {(monster.type === 'boss' || monster.type === 'super' || monster.type === 'dummy') && <div className="d2p-monster-id"/>}
      </div>
      {monster.type === 'champion' && (
        <div className="d2p-monster-options">
          <div className="d2p-monster-mod">
            <Select options={champions}
                    value={champions.find(t => t.id === monster.champion)}
                    onChange={onChangeChampion}
                    />
          </div>
          <div className="d2p-monster-mod"/>
          <div className="d2p-monster-mod"/>
        </div>
      )}
      {!!presetMods?.length && (
        <div className="d2p-monster-options">
          {[0, 1, 2, 3].map(idx => renderMod(idx, true))}
        </div>
      )}
      {!!extraMods && (
        <div className="d2p-monster-options">
          {[0, 1, 2].map(idx => renderMod(idx, false))}
        </div>
      )}
      {!!(health && health.min < health.max) && (
        <div className="d2p-monster-options d2p-center">
          <div className="d2p-monster-prop">{_L`Base Life`}:</div>
          <div className="d2p-monster-value">
            <NumberInput min={health.min} max={health.max} value={monster.maxhp} onChange={onChangeHealth}/>
            <span className="d2p-tip"> ({health.min}{'\u2013'}{health.max})</span>
          </div>
          <div className="d2p-monster-id"/>
        </div>
      )}
      {!!(attacks && attacks.length > 1) && (
        <div className="d2p-monster-options d2p-center">
          <div className="d2p-monster-prop">{_L`Attack Mode`}:</div>
          <div className="d2p-monster-value">
            <Select options={attackOptions} value={attackOptions.find(opt => opt.id === monster.attack)} onChange={onChangeAttack}/>
          </div>
          <div className="d2p-monster-id"/>
        </div>
      )}
      {!!monInfo?.inventory && <MonsterItems id={monId} monster={monster} difficulty={difficulty} playerLevel={playerLevel}/>}
    </div>
  );
});
